import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { EmbeddedStateChangedEvent } from '@/url/events/EmbeddedStateChangedEvent';
import { ActionCommitDegree } from './ActionCommitDegree';
import { IGetters } from './interfaces/IGetters';
import { IMutations } from './interfaces/IMutations';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { Organisation } from '@/models/Organisation';
import { IActions } from './interfaces/IActions';
import { PaginationDefinition } from './PaginationDefinition';
import { PaginationStateChangedRequestedEvent } from './events/PaginationStateChangedRequestedEvent';
import { ActionCommitPagination } from './ActionCommitPagination';

export class Actions implements ISubscriber<EmbeddedStateChangedEvent>, IActions {
	private readonly eventAggregationService: EventAggregationService;
	private readonly actionCommitDegree: ActionCommitDegree;
	private readonly actionCommitPagination: ActionCommitPagination;
	private readonly mutations: IMutations;
	private readonly getters: IGetters;
	private readonly subscribedEvents: string[] = [];

	public constructor(getters: IGetters, mutations: IMutations, eventAggregationService: EventAggregationService) {
		this.eventAggregationService = eventAggregationService;
		this.mutations = mutations;
		this.getters = getters;

		this.actionCommitDegree = new ActionCommitDegree(getters, mutations, this.eventAggregationService);
		this.actionCommitPagination = new ActionCommitPagination(getters, mutations, this.eventAggregationService);

		this.subscribeToEvents();
	}

	private subscribeToEvents(): void {
		this.eventAggregationService.subscribeTo(EmbeddedStateChangedEvent.EventType, this);
		this.subscribedEvents.push(EmbeddedStateChangedEvent.EventType);
	}

	public notify(event: EmbeddedStateChangedEvent): void {
		this.actionCommitDegree.commit(event.state.degreeOrNull, event.correlationId);
		this.actionCommitPagination.commit(event.state.pageOrNull, event.correlationId);
	}

	public commitSearch(organisations: readonly Organisation[], totalHits: number): void {
		this.mutations.setOrganisations(organisations);
		this.mutations.setTotalSearchResults(totalHits);
	}

	public clearSearch(): void {
		this.mutations.setOrganisations([]);
		this.mutations.setTotalSearchResults(0);
	}

	public determineTotalPages(): number {
		const count = this.getters.getTotalSearchResults();
		if (count === undefined) {
			return 0;
		}
		return Math.ceil(count / PaginationDefinition.RESULTS_PER_PAGE);
	}

	public incrementPageNumber(): void {
		this.publishPageChangeRequested(this.getters.getCurrentPageNumber() + 1);
	}

	public decrementPageNumber(): void {
		this.publishPageChangeRequested(this.getters.getCurrentPageNumber() - 1);
	}

	private publishPageChangeRequested(page: number): void {
		this.eventAggregationService.publishTo(
			PaginationStateChangedRequestedEvent.EventType,
			new PaginationStateChangedRequestedEvent(page)
		);
	}

	public resetState(): void {
		this.clearSearch();
		this.mutations.reset();
	}

}
